import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/site/Brand";
import { bk } from "@/lib/booking";
import { loadGA, track } from "@/lib/seo";
import landing from "@/data/landing.json";

const NAV = [["/tjenester", "Tjenester"], ["/priser", "Priser"], ["/fast-renholdsavtale", "Fast avtale"], ["/omrader", "Områder"], ["/kontakt", "Kontakt"], ["/prospect-finder", "Kundepilot"]];
const nav = ({ isActive }) => `text-sm font-semibold transition-colors ${isActive ? "text-[#2F5D50]" : "text-stone-600 hover:text-[#2F5D50]"}`;
export const usePublicConfig = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { bk.publicConfig().then(setCfg).catch(() => setCfg({})); }, []);
  return cfg;
};

const Consent = ({ gaId }) => {
  const [show, setShow] = useState(!localStorage.getItem("consent") && !!gaId);
  if (!show) return null;
  const pick = (v) => { localStorage.setItem("consent", v); setShow(false); if (v === "yes") loadGA(gaId); };
  return (
    <div className="fixed bottom-4 inset-x-4 z-50 mx-auto max-w-xl rounded-2xl border border-stone-200 bg-white p-4 shadow-xl text-sm" data-testid="consent-banner">
      Vi vil gjerne bruke Google Analytics for å forstå hvordan nettsiden brukes. Ingenting måles før du sier ja. <Link to="/personvern" className="underline">Personvern</Link>
      <div className="mt-3 flex gap-2">
        <button data-testid="consent-accept" onClick={() => pick("yes")} className="rounded-full bg-[#2F5D50] px-4 py-1.5 font-bold text-white">Godta</button>
        <button data-testid="consent-decline" onClick={() => pick("no")} className="rounded-full border border-stone-300 px-4 py-1.5 font-semibold">Avslå</button>
      </div>
    </div>
  );
};

export const CtaButtons = ({ center }) => (
  <div className={`flex flex-wrap gap-3 ${center ? "justify-center" : ""}`}>
    <Link to="/bestill" onClick={() => track("click_book_once")} data-testid="cta-book-once" className="rounded-full bg-[#2F5D50] px-7 py-3.5 font-bold text-white hover:bg-[#244a40] transition-colors">
      Bestill engangsrenhold
    </Link>
    <Link to="/fast-renholdsavtale" onClick={() => track("click_recurring")} data-testid="cta-recurring" className="rounded-full border-2 border-[#2F5D50] bg-white px-7 py-3 font-bold text-[#2F5D50] hover:bg-[#E7EFE9] transition-colors">
      Få fast renholdsavtale
    </Link>
  </div>
);

export const SiteLayout = () => {
  const cfg = usePublicConfig();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);
  useEffect(() => {
    if (cfg?.ga4_id) loadGA(cfg.ga4_id);
    if (cfg?.gsc_verification && !document.querySelector('meta[name="google-site-verification"]')) {
      const m = document.createElement("meta");
      m.name = "google-site-verification";
      m.content = cfg.gsc_verification;
      document.head.appendChild(m);
    }
  }, [cfg]);

  return (
    <div className="min-h-screen bg-[#FAF8F4] text-stone-800">
      <div className="bg-[#1F3D35] text-white text-xs sm:text-sm" data-testid="top-product-bar">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-2">
          <span className="font-semibold">Ask-Stokke Renhold <span className="opacity-60">·</span> <span className="opacity-80">Kundepilot</span></span>
          <Link to="/prospect-finder" data-testid="top-kundepilot-button" className="rounded-full bg-white/15 px-3 py-1 font-bold hover:bg-white/25 transition-colors">
            Kundepilot for renholdsbedrifter →
          </Link>
        </div>
      </div>
      <header className="sticky top-0 z-40 border-b border-stone-200/70 bg-[#FAF8F4]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-5 py-3.5">
          <Link to="/" data-testid="site-logo" className="group flex items-center gap-2.5 font-heading text-lg font-extrabold tracking-tight text-stone-900">
            <span className="transition-transform group-hover:rotate-6"><Logo /></span>
            <span>Ask-Stokke<span className="text-[#2F5D50]"> Renhold</span></span>
          </Link>
          <nav className="ml-auto hidden lg:flex items-center gap-6" aria-label="Hovedmeny">
            {NAV.map(([to, l]) => <NavLink key={to} to={to} className={nav}>{l}</NavLink>)}
            <NavLink to="/min-side" className={nav} data-testid="site-nav-account">Min side</NavLink>
          </nav>
          <Link to="/bestill" onClick={() => track("click_book_once")} data-testid="site-nav-book" className="ml-auto lg:ml-0 rounded-full bg-[#2F5D50] px-5 py-2 text-sm font-bold text-white hover:bg-[#244a40]">Bestill</Link>
          <button className="lg:hidden" aria-label="Meny" data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <nav className="lg:hidden border-t border-stone-200 bg-white px-5 py-3 grid gap-3" data-testid="mobile-menu">
            {[...NAV, ["/min-side", "Min side"]].map(([to, l]) => <NavLink key={to} to={to} className={nav}>{l}</NavLink>)}
          </nav>
        )}
      </header>
      <Outlet context={cfg} />
      <footer className="mt-24 border-t border-stone-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 sm:grid-cols-2 lg:grid-cols-4 text-sm text-stone-600">
          <div>
            <div className="font-heading font-extrabold text-stone-900">Ask-Stokke Renhold</div>
            <p className="mt-2">Rent hjem. Mindre unødvendig kjemi.</p>
            <a href="tel:+4748506770" className="mt-3 flex items-center gap-1.5 hover:text-[#2F5D50]"><Phone className="h-4 w-4" /> 485 06 770</a>
            <a href="mailto:askstokke.renhold@gmail.com" className="block hover:text-[#2F5D50]">askstokke.renhold@gmail.com</a>
            {cfg?.org_nr && <div className="mt-1">Org.nr. {cfg.org_nr}</div>}
          </div>
          <div className="space-y-1.5">
            <div className="font-semibold text-stone-900">Selskapet</div>
            {[["/om-oss", "Om oss"], ["/renhold-og-helse", "Renhold og helse"], ["/sporsmal-og-svar", "Ofte stilte spørsmål"], ["/personvern", "Personvern"], ["/vilkar", "Vilkår"]].map(([to, l]) => <Link key={to} to={to} className="block hover:text-[#2F5D50]">{l}</Link>)}
          </div>
          <div className="space-y-1.5">
            <div className="font-semibold text-stone-900">Områder</div>
            {landing.filter((l) => l.slug.startsWith("renhold-")).map((l) => <Link key={l.slug} to={`/${l.slug}`} className="block hover:text-[#2F5D50]">Renhold {l.place}</Link>)}
          </div>
          <div className="space-y-1.5">
            <div className="font-semibold text-stone-900">Populært</div>
            {landing.filter((l) => !l.slug.startsWith("renhold-")).map((l) => <Link key={l.slug} to={`/${l.slug}`} className="block hover:text-[#2F5D50]">{l.h1}</Link>)}
            <Link to="/login" className="block pt-3 text-xs text-stone-400 hover:text-stone-600">For ansatte</Link>
          </div>
        </div>
      </footer>
      <Consent gaId={cfg?.ga4_id} />
    </div>
  );
};
